/**
 * Tracker-style ambient music.
 * Each track is a short repeating pattern of notes scheduled via WebAudio
 * oscillators with simple envelopes. No samples, no files.
 *
 * A "song" is: { bpm, lead[], bass[], drums[] }.
 *   - lead/bass arrays are step indices → MIDI note (or 0 = rest).
 *   - drums array is step indices → 'k' (kick) | 's' (snare) | 'h' (hat) | 0.
 *
 * Songs live as one JSON file per track under `./songs/*.json` and are
 * picked deterministically by level id so each level always plays the
 * same tune. Looping is open-ended: we schedule one bar ahead of the
 * audio clock and refill on a setInterval.
 */

import { getAudioContext } from './Sfx.js';

type DrumHit = 0 | 'k' | 's' | 'h';

export interface Song {
  /** Stable id used by share-links and the jukebox. Matches the JSON file basename. */
  id?: string;
  name: string;
  bpm: number;
  /** lead voice: triangle, gentle. 0 = rest, otherwise MIDI note. */
  lead: number[];
  /** bass voice: square at low octave. */
  bass: number[];
  /** drum row, sample-free synthesized hits. */
  drums: DrumHit[];
  /** master gain (0..1). */
  gain?: number;
}

const STEPS = 16; // 16 steps per bar (sixteenth notes at 4/4)

// MIDI helpers
const midiToHz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

// ---------------------------------------------------------------------------
// Songs. Pentatonic / dorian-flavored loops to stay friendly under any swing.
// One JSON file per song under `./songs/`. Sorted by filename so the order
// stays deterministic across builds (used by `pickSong` hashing below).
// ---------------------------------------------------------------------------

const SONG_MODULES = import.meta.glob('./songs/*.json', { eager: true }) as Record<
  string,
  { default: Song }
>;

const SONGS: Song[] = Object.keys(SONG_MODULES)
  .sort()
  .map((k) => SONG_MODULES[k]!.default);


/** Stable pick by level id so each level always sounds the same. */
function pickSong(levelId: string): Song {
  let h = 0;
  for (let i = 0; i < levelId.length; i++) h = (h * 31 + levelId.charCodeAt(i)) >>> 0;
  return SONGS[h % SONGS.length]!;
}

/** Public alias of `pickSong` so callers (e.g. share-link builder) can
 *  ask "what song would this level play by default?" without duplicating
 *  the hashing logic. */
export function pickSongForLevel(levelId: string): Song {
  return pickSong(levelId);
}

/** Read-only access to the full song list (used by the jukebox UI). */
export function getSongs(): readonly Song[] {
  return SONGS;
}

// ---------------------------------------------------------------------------
// Player
// ---------------------------------------------------------------------------

export class Music {
  private song: Song;
  private master: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  private nextStepTime = 0;
  private step = 0;
  private startTime = 0;
  private timer: number | null = null;
  private muted = false;
  /** Pending deferred-start listener (registered when start() is called
   *  before the audio context has been unlocked). Tracked so stop() can
   *  detach it — otherwise calling start() then stop() before the user's
   *  first gesture leaves a zombie listener that resurrects playback when
   *  the context finally resumes. This was the iPad "ghost music" bug:
   *  every level swap leaked a listener and they all fired together on
   *  the first tap, layering several tracks on top of each other. */
  private pendingStateListener: (() => void) | null = null;

  /** Construct from a level id (deterministic pick) or directly from a song. */
  constructor(idOrSong: string | Song) {
    this.song = typeof idOrSong === 'string' ? pickSong(idOrSong) : idOrSong;
  }

  /** Lazily create + return an AnalyserNode that the master mix is routed
   *  through, so the jukebox UI can draw a live frequency-domain visualizer.
   *  Safe to call before or after start(). */
  getAnalyser(): AnalyserNode {
    const a = getAudioContext();
    if (!this.analyser) {
      this.analyser = a.createAnalyser();
      this.analyser.fftSize = 64;
      this.analyser.smoothingTimeConstant = 0.7;
      // If we're already playing, splice the analyser in: master -> analyser -> destination.
      if (this.master) {
        try { this.master.disconnect(); } catch { /* ignore */ }
        this.master.connect(this.analyser).connect(a.destination);
      }
    }
    return this.analyser;
  }

  start(): void {
    if (this.timer !== null) return;
    const a = getAudioContext();
    // If the context hasn't been unlocked yet, defer start until it is.
    if (a.state !== 'running') {
      // Replace any previous pending listener so repeated start() calls
      // before unlock don't stack up.
      if (this.pendingStateListener) {
        a.removeEventListener('statechange', this.pendingStateListener);
      }
      const onState = () => {
        if (a.state === 'running') {
          a.removeEventListener('statechange', onState);
          this.pendingStateListener = null;
          this.start();
        }
      };
      this.pendingStateListener = onState;
      a.addEventListener('statechange', onState);
      return;
    }
    this.master = a.createGain();
    this.master.gain.value = this.muted ? 0 : (this.song.gain ?? 0.12);
    if (this.analyser) {
      this.master.connect(this.analyser).connect(a.destination);
    } else {
      this.master.connect(a.destination);
    }
    this.nextStepTime = a.currentTime + 0.05;
    this.step = 0;
    this.startTime = this.nextStepTime;
    this.timer = window.setInterval(() => this.scheduleAhead(), 30);
    this.scheduleAhead();
  }

  stop(): void {
    // Cancel a pending deferred start (see pendingStateListener docs).
    if (this.pendingStateListener) {
      try {
        getAudioContext().removeEventListener('statechange', this.pendingStateListener);
      } catch { /* ignore */ }
      this.pendingStateListener = null;
    }
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
    if (this.master) {
      try {
        const a = getAudioContext();
        this.master.gain.cancelScheduledValues(a.currentTime);
        this.master.gain.linearRampToValueAtTime(0, a.currentTime + 0.05);
        const g = this.master;
        setTimeout(() => g.disconnect(), 80);
      } catch { /* ignore */ }
      this.master = null;
    }
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.master) {
      this.master.gain.value = m ? 0 : (this.song.gain ?? 0.12);
    }
  }

  /** Re-read `song.gain` and apply it to the live master node. Lets the
   *  Jukebox editor scrub gain without restarting playback. No-op when
   *  not playing or muted. */
  refreshGain(): void {
    if (this.master && !this.muted) {
      this.master.gain.value = this.song.gain ?? 0.12;
    }
  }

  /** Currently audible step index in [0,16), or -1 if not playing. Used by
   *  the jukebox tracker view to highlight the playhead. */
  getCurrentStep(): number {
    if (!this.master || this.timer === null) return -1;
    const a = getAudioContext();
    const stepDur = 60 / this.song.bpm / 4;
    const elapsed = a.currentTime - this.startTime;
    if (elapsed < 0) return -1;
    return Math.floor(elapsed / stepDur) % 16;
  }

  /** The song this player is rendering. Exposed so the jukebox tracker view
   *  can read the lead/bass/drum step arrays without reaching for getSongs. */
  getSong(): Song {
    return this.song;
  }

  /** Look-ahead scheduler. Schedules ~120 ms of audio at a time.
   *
   *  PWA / backgrounding guard: when the page is hidden the AudioContext
   *  is suspended (see Sfx.installVisibilityHandler) so `currentTime`
   *  stops advancing while the setInterval keeps firing on the timer
   *  thread. On resume `nextStepTime` may be many seconds behind the new
   *  `currentTime`; without a clamp the while loop would schedule the
   *  entire backlog of steps in one tick — audible as a chord burst.
   *  We snap forward whenever we've fallen more than one step behind. */
  private scheduleAhead(): void {
    if (!this.master) return;
    const a = getAudioContext();
    if (a.state !== 'running') return;
    const stepDur = 60 / this.song.bpm / 4; // 16th-note seconds
    if (this.nextStepTime < a.currentTime - stepDur) {
      // Realign on the next bar boundary so we don't drop in mid-beat.
      const stepsAhead = Math.ceil((a.currentTime + 0.05 - this.startTime) / stepDur);
      this.step = ((stepsAhead % STEPS) + STEPS) % STEPS;
      this.nextStepTime = this.startTime + stepsAhead * stepDur;
    }
    const horizon = a.currentTime + 0.12;
    while (this.nextStepTime < horizon) {
      this.scheduleStep(this.nextStepTime, this.step);
      this.nextStepTime += stepDur;
      this.step = (this.step + 1) % STEPS;
    }
  }

  private scheduleStep(time: number, step: number): void {
    const a = getAudioContext();
    if (!this.master) return;
    const dest = this.master;
    const stepDur = 60 / this.song.bpm / 4;

    // Lead
    const leadNote = this.song.lead[step] ?? 0;
    if (leadNote) playNote(a, dest, midiToHz(leadNote), time, stepDur * 1.4, 'triangle', 0.6);

    // Bass
    const bassNote = this.song.bass[step] ?? 0;
    if (bassNote) playNote(a, dest, midiToHz(bassNote), time, stepDur * 2.0, 'square', 0.35);

    // Drums
    const d = this.song.drums[step];
    if (d === 'k') playKick(a, dest, time, 0.6);
    else if (d === 's') playSnare(a, dest, time, 0.35);
    else if (d === 'h') playHat(a, dest, time, 0.18);
  }
}

// --- Synth voices --------------------------------------------------------------

function playNote(
  a: AudioContext, dest: AudioNode, freq: number, t0: number, dur: number,
  type: OscillatorType, gain: number,
): void {
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(dest);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function playKick(a: AudioContext, dest: AudioNode, t0: number, gain: number): void {
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(120, t0);
  osc.frequency.exponentialRampToValueAtTime(40, t0 + 0.12);
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
  osc.connect(g).connect(dest);
  osc.start(t0);
  osc.stop(t0 + 0.2);
}

function playSnare(a: AudioContext, dest: AudioNode, t0: number, gain: number): void {
  const buf = getNoiseBuffer(a, 0.12);
  const src = a.createBufferSource();
  src.buffer = buf;
  const hp = a.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1200;
  const g = a.createGain();
  g.gain.value = gain;
  src.connect(hp).connect(g).connect(dest);
  src.start(t0);
}

function playHat(a: AudioContext, dest: AudioNode, t0: number, gain: number): void {
  const buf = getNoiseBuffer(a, 0.04);
  const src = a.createBufferSource();
  src.buffer = buf;
  const hp = a.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 6000;
  const g = a.createGain();
  g.gain.value = gain;
  src.connect(hp).connect(g).connect(dest);
  src.start(t0);
}

// Cache decaying-noise buffers (keyed by exact length-in-samples) so repeated
// drum hits don't allocate a fresh AudioBuffer + Float32 fill loop each step.
const noiseBufferCache = new Map<number, AudioBuffer>();
function getNoiseBuffer(a: AudioContext, durSec: number): AudioBuffer {
  const len = Math.floor(a.sampleRate * durSec);
  const cached = noiseBufferCache.get(len);
  if (cached) return cached;
  const buf = a.createBuffer(1, len, a.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  noiseBufferCache.set(len, buf);
  return buf;
}
